import { BarChart3, CalendarRange, TrendingUp, UserX, Wallet } from "lucide-react";
import { useState } from "react";
import { Navigate, useOutletContext } from "react-router-dom";

import { Card, EmptyState, Input, Spinner } from "@/components/ui";
import { addDays, isoDate } from "@/lib/format";
import { useFormat, useT, type Chave } from "@/lib/i18n";
import { useRevenueReport, type Period } from "@/lib/queries";
import type { Me } from "@/lib/types";

/** O período que a tela abre: o fechamento do dono é mensal, e é o mês
 *  corrente que ele confere em quase toda visita. */
function esteMes(): Period {
  const hoje = new Date();
  return {
    date_from: isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
    date_to: isoDate(hoje),
  };
}

/** Atalhos de período.
 *
 * t("reports.lastMonth") existe pelo mesmo motivo do default: nos primeiros dias do
 * mês, o número que o dono quer conferir é o do mês que acabou de fechar, e
 * montá-lo à mão nos dois campos de data é o tipo de fricção que faz a tela
 * não ser usada.
 */
/** `label` é chave: a constante é avaliada no import e congelaria o
 *  idioma daquele instante. */
const ATALHOS: { label: Chave; period: () => Period }[] = [
  {
    label: "agenda.todayShort",
    period: () => ({ date_from: isoDate(new Date()), date_to: isoDate(new Date()) }),
  },
  {
    label: "reports.sevenDays",
    period: () => ({
      date_from: isoDate(addDays(new Date(), -6)),
      date_to: isoDate(new Date()),
    }),
  },
  { label: "reports.thisMonth", period: esteMes },
  {
    label: "reports.lastMonth",
    period: () => {
      const hoje = new Date();
      return {
        date_from: isoDate(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
        // Dia 0 do mês corrente é o último dia do mês anterior.
        date_to: isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), 0)),
      };
    },
  },
];

export default function Reports() {
  const t = useT();
  const { money } = useFormat();
  const me = useOutletContext<Me>();
  const [period, setPeriod] = useState<Period>(esteMes);
  const { data, isLoading, isFetching, error } = useRevenueReport(period);

  // O backend responde 403 aqui (CanSeeFinancials). Barrar na rota também
  // evita que um profissional que digitou /relatorios veja a tela montar e
  // depois quebrar.
  if (!me.permissions.see_financials) return <Navigate to="/" replace />;

  const total = Number(data?.summary.revenue ?? 0);
  const fatia = (valor: string) => (total > 0 ? `${Math.round((Number(valor) / total) * 100)}%` : "—");

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-5">
        <h1 className="text-xl font-semibold lg:text-2xl">{t("nav.reports")}</h1>
        <p className="text-sm text-steel-muted">{t("reports.completedOnly")}</p>
      </div>

      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {ATALHOS.map((atalho) => {
              const alvo = atalho.period();
              const ativo =
                alvo.date_from === period.date_from && alvo.date_to === period.date_to;
              return (
                <button
                  key={atalho.label}
                  onClick={() => setPeriod(alvo)}
                  className={
                    ativo
                      ? "rounded-[4px] border border-nitro-600/40 bg-nitro-500/10 px-3 py-2 text-xs font-semibold text-nitro-400"
                      : "rounded-[4px] border border-line px-3 py-2 text-xs font-semibold text-steel-muted transition hover:border-steel-dim hover:text-steel"
                  }
                >
                  {t(atalho.label)}
                </button>
              );
            })}
          </div>

          <div className="flex flex-1 flex-wrap items-end gap-3">
            <Input
              label={t("reports.from")}
              type="date"
              className="min-w-[9.5rem]"
              value={period.date_from}
              max={period.date_to}
              onChange={(e) =>
                e.target.value && setPeriod({ ...period, date_from: e.target.value })
              }
            />
            <Input
              label={t("reports.to")}
              type="date"
              className="min-w-[9.5rem]"
              value={period.date_to}
              min={period.date_from}
              onChange={(e) => e.target.value && setPeriod({ ...period, date_to: e.target.value })}
            />
            {isFetching && <Spinner className="mb-3 size-4" />}
          </div>
        </div>
      </Card>

      {error ? (
        <Card>
          <EmptyState
            icon={<BarChart3 size={40} />}
            title={t("reports.loadFailed")}
            description={t("reports.checkPeriod")}
          />
        </Card>
      ) : isLoading || !data ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              icon={<Wallet size={18} />}
              label={t("reports.revenue")}
              value={money(data.summary.revenue)}
            />
            <Stat
              icon={<CalendarRange size={18} />}
              label={t("reports.appointments")}
              value={String(data.summary.completed)}
              hint={t("reports.booked", { n: data.summary.appointments })}
            />
            <Stat
              icon={<TrendingUp size={18} />}
              label={t("reports.avgTicket")}
              value={money(data.summary.average_ticket)}
              hint={t("reports.perAppointment")}
            />
            <Stat
              icon={<UserX size={18} />}
              label={t("reports.noShows")}
              value={String(data.summary.no_shows)}
              hint={
                data.summary.canceled > 0
                  ? t("reports.canceledCount", { n: data.summary.canceled })
                  : undefined
              }
            />
          </div>

          {data.summary.completed === 0 ? (
            <Card className="mt-6">
              <EmptyState
                icon={<BarChart3 size={40} />}
                title={t("reports.noneCompleted")}
                description={t("reports.noneCompletedHint")}
              />
            </Card>
          ) : (
            <>
              <Tabela
                title={t("reports.byTech")}
                subtitle={t("reports.byTechHint")}
                head={[
                  t("modal.technician"),
                  t("reports.appointmentsShort"),
                  t("reports.revenue"),
                  "%",
                  t("reports.commission"),
                  t("reports.noShows"),
                ]}
                rows={data.by_professional.map((p) => [
                  <span key="n" className="flex items-center gap-2">
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: p.color }}
                    />
                    <span className="truncate">{p.name}</span>
                  </span>,
                  p.appointments,
                  money(p.revenue),
                  fatia(p.revenue),
                  money(p.commission),
                  p.no_shows || "—",
                ])}
              />

              <Tabela
                title={t("reports.byService")}
                subtitle={t("reports.byServiceHint")}
                head={[t("modal.services"), t("reports.qty"), t("reports.revenue"), "%"]}
                rows={data.by_service.map((s) => [
                  s.name,
                  s.quantity,
                  money(s.revenue),
                  fatia(s.revenue),
                ])}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-steel-muted">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
      {hint && <p className="mt-1 text-xs text-steel-dim">{hint}</p>}
    </Card>
  );
}

/** Tabela de verdade, e não lista de cartões como no resto do painel: aqui
 *  o dono compara linhas entre si, e comparar exige colunas alinhadas. No
 *  celular ela rola na horizontal — encolher a fonte até caber deixaria o
 *  número ilegível justamente para quem confere no balcão. */
function Tabela({
  title,
  subtitle,
  head,
  rows,
}: {
  title: string;
  subtitle: string;
  head: string[];
  rows: React.ReactNode[][];
}) {
  return (
    <Card className="mt-6">
      <div className="border-b border-line px-5 py-4">
        <h2 className="font-medium">{title}</h2>
        <p className="text-sm text-steel-muted">{subtitle}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-steel-dim">
              {head.map((coluna, i) => (
                <th
                  key={coluna}
                  scope="col"
                  className={`px-5 py-2.5 font-medium ${i === 0 ? "text-left" : "text-right"}`}
                >
                  {coluna}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((linha, i) => (
              <tr key={i} className="border-t border-line">
                {linha.map((celula, j) => (
                  <td
                    key={j}
                    className={`px-5 py-3 whitespace-nowrap ${
                      j === 0 ? "text-left font-medium" : "text-right tabular-nums"
                    }`}
                  >
                    {celula}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
